/**
 * The adapter GrantAccessModal talks to the server through (feature 065).
 *
 * The modal takes an `api` rather than importing axios itself, so it stays presentational and
 * its tests need no HTTP mocking. Four screens now open that modal — /users, User 360, and the
 * two survey review tabs — and each of them already owns a JWT axios instance and a bearer
 * token, so the only thing worth sharing is this pair of calls. Written once, it also means
 * the two endpoints are named in one place: a route rename is one edit, not four.
 *
 * Errors are unwrapped to the server's own message. "A reason is required" is of more use to an
 * admin than "400", and the modal renders whatever it is handed.
 *
 * @param {import('axios').AxiosInstance} axiosJWT instance carrying the refresh interceptor
 * @param {string} token current bearer token
 * @param {string} baseUrl API origin
 */
export default function createGrantApi(axiosJWT, token, baseUrl) {
  const auth = () => ({ headers: { Authorization: `Bearer ${token}` } });

  return {
    preview: async (params) => {
      try {
        const res = await axiosJWT.get(`${baseUrl}/admin/api/free-access-grants/preview`, {
          params,
          ...auth(),
        });
        return res.data?.data;
      } catch (e) {
        throw new Error(e.response?.data?.message || 'Could not load the preview.');
      }
    },
    create: async (body) => {
      try {
        const res = await axiosJWT.post(`${baseUrl}/admin/api/free-access-grants`, body, auth());
        return res.data?.data?.grant;
      } catch (e) {
        throw new Error(e.response?.data?.message || 'Could not save the grant.');
      }
    },
  };
}

/**
 * The one sentence every screen shows after a grant. `billing_message` carries the part an
 * admin may need to act on — a failed store call still means the member HAS their access, and
 * that distinction has to survive to the screen rather than stopping at the log.
 */
export const grantedMessage = (grant) => (grant?.billing_message
  ? `Free access granted. ${grant.billing_message}`
  : 'Free access granted.');
